import { Building2, CreditCard, LogOut, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useTaggedVersion } from "@/hooks/use-version";
import { useNavigate } from "react-router-dom";
import { LocalRoutes } from "@/LocalRoutes";

export const NavUser = ({
    user,
    logout,
}: {
    user: {
        name: string;
        email: string;
        avatar: string;
        isAdmin: boolean;
    };
    logout: () => void;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data: taggedAppVersion } = useTaggedVersion();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    aria-label={t("components.nav-user.menu")}
                    className="flex h-auto items-center px-2 data-[state=open]:bg-accent"
                    variant="ghost"
                >
                    <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarImage alt={user.name} src={user.avatar} />
                        <AvatarFallback className="rounded-lg bg-neutral-500 text-white">
                            {user.name?.[0] ?? "-"}
                        </AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                sideOffset={4}
            >
                <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <Avatar className="h-8 w-8 rounded-lg">
                            <AvatarImage alt={user.name} src={user.avatar} />
                            <AvatarFallback className="rounded-lg">
                                {user.name?.[0] ?? "-"}
                            </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                            <span className="truncate font-semibold">
                                {user.name}
                            </span>
                            <span className="truncate text-xs">
                                {user.email}
                            </span>
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={() => void navigate(LocalRoutes.USER_DATA)}
                >
                    <User className="mr-2 h-4 w-4" />
                    {t("components.nav-user.personal-data")}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => void navigate(LocalRoutes.LEGAL_PERSONS)}
                >
                    <Building2 className="mr-2 h-4 w-4" />
                    {t("components.nav-user.legal-persons")}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => void navigate(LocalRoutes.BILLING)}
                >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t("components.nav-user.payment-infos")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("components.nav-user.logout")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={() => {
                        window.open(
                            "https://energiegemeinschaft.info/datenschutzerklarung/",
                            "_blank",
                        );
                    }}
                >
                    {t("components.nav-user.privacy-policy")}
                </DropdownMenuItem>
                {user.isAdmin ? (
                    <DropdownMenuLabel>{taggedAppVersion}</DropdownMenuLabel>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
